import { config } from '../../../config';
import type { PublicClientMessage } from '../../api/CoreApi';
import { getMeMessages } from '../../features/queries/getMeMessages';
import { publicGetClientMessages } from '../../features/queries/publicGetClientMessages';
import {
  bySeverity,
  dismissKey,
  MESSAGE_TONE,
  messagesTopic,
  safeMarkdownUrl,
} from '../../lib/messages';
import { useQ } from '../../lib/query';
import { useMqtt } from '../../providers/MqttClientProvider';
import { __ } from '../../utils';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Banner,
  Box,
  ButtonBase,
  MarkdownPreview,
  Popover,
  Stack,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Banner so správami platformy nad hlavičkou.
 *
 * Dve cesty, ako to zadanie predpisuje:
 *
 * - **REST je nosný.** Funguje pre neprihláseného návštevníka (AuthProvider má
 *   `optional`), aj keď je broker dole, aj keď firemná proxy blokuje
 *   WebSockety. Prihlásený dostane `/me/messages` (aj cielené na jeho roly),
 *   neprihlásený verejný endpoint (len `audience: all`).
 * - **MQTT je živá aktualizácia.** Ping na `system/messages/<clientId>` nesie
 *   len odtlačok množiny, nie obsah - cielené správy sa nesmú vysielať na
 *   topic, ktorý odoberá každý prihlásený. Pri zmene odtlačku sa obsah
 *   dotiahne REST-om, kde ho server filtruje podľa tokenu.
 *
 * Zobrazuje sa **jedna** správa, najvážnejšia; ostatné sú za „+N ďalších".
 * Hlavička tak má stálu výšku aj pri desiatke správ.
 *
 * Zavretie správy platí len do reloadu - dôvod je pri `dismissed` nižšie.
 *
 * Nahradilo to natvrdo napísaný pás o testovacom režime za prepínačom
 * `CUSTOM_SHOW_DEV_BANNER`. Ten text bol chvíľu ponechaný ako fallback, ale
 * duplikoval to, čo sa dá zadať v cimpe - a per-prostredie prepínač je horší
 * zdroj pravdy než správa v databáze, ktorú vidí správca. Keď niet správy,
 * niet ani pásu.
 */

const MessageBody: React.FC<{ text: string; compact?: boolean }> = ({
  text,
  compact,
}) => (
  <Box
    sx={(theme) => ({
      minWidth: 0,
      // MarkdownPreview kreslí blokové elementy s marginmi a vlastnou
      // veľkosťou písma; Banner je pásik s 13 px.
      '& .wmde-markdown': {
        background: 'transparent',
        fontSize: 'inherit',
        color: 'inherit',
        // Skracovanie patrí na tento element, nie na obal: obal v sebe má
        // blok (`.wmde-markdown`), a `text-overflow` reže len riadkový obsah,
        // takže výpustka sa na obale nikdy nevykreslí - text sa len odstrihol
        // na hrane okna.
        //
        // Na mobile sa správa **zabalí**; jeden riadok na 360 px zobrazí zo
        // správy pár slov a zvyšok je nedostupný, kým je celá správa to jediné,
        // čo pás nesie. Päť riadkov je strop, aby dlhý text nezjedol obrazovku;
        // výška pásu je meraná (`--motd-h`), takže zabalenie nič nerozhodí.
        // Na širokom displeji ostáva jeden riadok - tam sa správa zmestí a pás
        // by inak menil výšku pri každom preklade textu.
        ...(compact
          ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 5,
              overflow: 'hidden',
              [theme.breakpoints.up('md')]: { WebkitLineClamp: 1 },
            }
          : {}),
      },
      '& p': { margin: 0, ...(compact ? { display: 'inline' } : {}) },
    })}
  >
    <MarkdownPreview
      value={text}
      urlTransform={safeMarkdownUrl}
      disallowedElements={['img']}
      style={{ background: 'transparent', fontSize: 13 }}
    />
  </Box>
);

const MessagesBanner: React.FC = () => {
  const { authUser } = useAuth();
  const { subscribe } = useMqtt();
  const isAuthenticated = !!authUser?.clientAccountId;

  /**
   * Zavreté správy **len tejto session**. Po reloade sa správa zjaví znova -
   * zámerne, preto sa zavretie nikam neukladá.
   *
   * Je to prevádzkové oznámenie (odstávka, výpadok), nie marketingový pás:
   * kým platí, má byť vidieť. Zavretie je úľava „prekrýva mi to obsah, už som
   * to čítal", nie trvalé odhlásenie - trvalé by znamenalo, že správca nemá
   * ako oznámenie doručiť tomu, kto ho raz zavrel.
   *
   * Kľúč aj tak nesie `updated_at` (`dismissKey`), takže úprava textu správu
   * zjaví znova aj v rámci jednej session.
   */
  const [dismissed, setDismissed] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const bannerRef = React.useRef<HTMLDivElement | null>(null);

  // Prihlásený má vlastnú množinu (cielenie na roly), neprihlásený verejnú.
  // Sú to zámerne dva endpointy: `openapi-security-handler` je buď-alebo,
  // takže jeden s voliteľným tokenom sa spraviť nedá.
  const authed = useQ(getMeMessages, undefined, {
    enabled: isAuthenticated,
    refetchInterval: 5 * 60_000,
  });
  // Neprihlásený sa pýta častejšie zámerne. Pre prihláseného je polling len
  // poistka - o zmene mu povie MQTT ping. Anonym na broker nechodí (nemá čím
  // sa autentifikovať), takže tento interval je preňho jediná cesta, ako sa
  // o novej správe dozvedieť.
  const publicQ = useQ(publicGetClientMessages, undefined, {
    enabled: !isAuthenticated,
    refetchInterval: 60_000,
  });

  const active = isAuthenticated ? authed : publicQ;
  const data = active.data;
  const refetch = active.refetch;

  const nextChangeAt = data?.next_change_at;
  const version = data?.version;
  const lastSeenVersion = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    lastSeenVersion.current = version;
  }, [version]);

  /**
   * MQTT ping. Topic musí byť **konkrétny**, nie wildcard: `MqttClientProvider`
   * rozdeľuje správy podľa presnej zhody `handlersRef.current[topic]`, takže
   * `system/messages/+` by nedoručilo nič.
   *
   * Odber sa ruší v cleanupe. (`NotificationsProvider` to nerobí a remount tam
   * necháva mŕtvy handler - tú chybu tu nekopírujeme.)
   */
  React.useEffect(() => {
    // MqttClientProvider sa pripája len pre prihláseného (potrebuje jeho
    // access token ako heslo k brokeru). Pre neprihláseného by `subscribe` len
    // vypísalo varovanie do konzoly na každej stránke - a REST cesta pre neho
    // funguje aj tak.
    if (!isAuthenticated) {
      return;
    }

    const topic = messagesTopic(config.CLIENT_ID);
    return subscribe(topic, (payload) => {
      try {
        const { version: incoming } = JSON.parse(payload) as {
          version?: string;
        };
        // Retained ping príde hneď po prihlásení na odber, takže bez tejto
        // porovnávky by každé pripojenie znamenalo jeden zbytočný dotaz.
        if (incoming && incoming !== lastSeenVersion.current) {
          lastSeenVersion.current = incoming;
          refetch();
        }
      } catch {
        // Neznámy payload - radšej si vyžiadame stav, než by sme ho ignorovali.
        refetch();
      }
    });
  }, [isAuthenticated, subscribe, refetch]);

  /**
   * Naplánované okno platnosti. Server posiela najbližší okamih, kedy sa
   * množina zmení sama; prepne to presne na sekundu, kým scheduler na serveri
   * tiká raz za minútu.
   */
  React.useEffect(() => {
    if (!nextChangeAt) return;
    const delay = new Date(nextChangeAt).getTime() - Date.now();
    if (delay <= 0) {
      refetch();
      return;
    }
    if (delay > 2_147_483_647) return;
    const timer = window.setTimeout(() => refetch(), delay + 500);
    return () => window.clearTimeout(timer);
  }, [nextChangeAt, refetch]);

  const visible = React.useMemo(
    () =>
      [...(data?.messages ?? [])]
        .sort(bySeverity)
        .filter((message) => !dismissed.has(dismissKey(message))),
    [data?.messages, dismissed],
  );

  const rendersSomething = !!visible.length;

  /**
   * Výška bannera do CSS premennej `--motd-h`.
   *
   * Sticky offsety v `App.tsx` boli natvrdo dopočítané z prepínača
   * `CUSTOM_SHOW_DEV_BANNER`. Banner je dátový, takže jeho prítomnosť sa
   * dopredu nedá vypočítať - a markdown správa sa navyše môže zabaliť do
   * dvoch riadkov, takže ani konštanta 34 px by nestačila. Meria sa preto
   * naozaj.
   */
  React.useEffect(() => {
    const root = document.documentElement;
    const element = bannerRef.current;

    if (!rendersSomething || !element) {
      root.style.setProperty('--motd-h', '0px');
      return () => root.style.setProperty('--motd-h', '0px');
    }

    const observer = new ResizeObserver(([entry]) => {
      root.style.setProperty(
        '--motd-h',
        `${Math.round(entry.contentRect.height)}px`,
      );
    });
    observer.observe(element);
    root.style.setProperty(
      '--motd-h',
      `${Math.round(element.getBoundingClientRect().height)}px`,
    );

    return () => {
      observer.disconnect();
      root.style.setProperty('--motd-h', '0px');
    };
  }, [rendersSomething]);

  const dismiss = React.useCallback((message: PublicClientMessage) => {
    setDismissed((prev) => new Set(prev).add(dismissKey(message)));
  }, []);

  if (!visible.length) {
    return null;
  }

  const [first, ...rest] = visible;

  return (
    <>
      <Box
        ref={bannerRef}
        sx={{
          // `Banner` je flex a text dáva do `<span>`-u bez `min-width: 0`.
          // Flex item sa tým nesmie zmenšiť pod šírku svojho obsahu, takže
          // dlhá správa vytečie z pásu - na mobile sa odreže na hrane okna
          // a „Zavrieť" skončí mimo obrazovky. Povoliť to musí ten predok,
          // a appka doňho inak nevidí; `Banner` má na `<span>`-e len text.
          '& > [role="status"] > span': { minWidth: 0 },
        }}
      >
        <Banner
          tone={MESSAGE_TONE[first.type] ?? 'info'}
          onDismiss={first.dismissible ? () => dismiss(first) : undefined}
          dismissLabel={__('Zavrieť')}
        >
          <Stack direction='row' alignItems='center' gap={1} minWidth={0}>
            <MessageBody text={first.text} compact />
            {!!rest.length && (
              <ButtonBase
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={(theme) => ({
                  flex: 'none',
                  textDecoration: 'underline',
                  fontSize: theme.typography.pxToRem(12),
                  color: 'inherit',
                })}
              >
                {`+${rest.length} ${__('ďalších')}`}
              </ButtonBase>
            )}
          </Stack>
        </Banner>
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack sx={{ p: 1.5, gap: 1.5, maxWidth: 480 }}>
          {visible.map((message) => (
            <Stack
              key={message.message_id}
              direction='row'
              gap={1}
              alignItems='flex-start'
            >
              <Box
                component='span'
                sx={(theme) => ({
                  flex: 'none',
                  width: 6,
                  height: 6,
                  mt: 0.75,
                  borderRadius: `${theme.shape.borderRadiusFull}px`,
                  backgroundColor:
                    theme.palette[MESSAGE_TONE[message.type] ?? 'info'].dark,
                })}
              />
              <MessageBody text={message.text} />
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default MessagesBanner;
